import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Activity, Clock, Flame, Trophy, Target, Sparkles } from "lucide-react";
import LoadingState from "../components/LoadingState";
import { useTranslation } from "react-i18next";

export default function Stats() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/stats");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || t("stats.couldnt_load"));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  if (!data || error) return <LoadingState error={error} onRetry={load} rows={5} testid="stats" />;
  const p = data.profile;

  const sessionStatus = (s) => {
    if (s === "won") return t("stats.won");
    if (s === "lost") return t("stats.lost");
    return t("stats.abandoned");
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm uppercase tracking-[0.25em] text-[#8AA48F] mb-2">{t("stats.your_progress")}</p>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-4xl sm:text-5xl font-light tracking-tight">
          {t("stats.headline")}
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard testid="stat-calories" icon={Flame} color="#D96C6C" label={t("stats.calories_burned")} value={Math.round(p.calories_burned)} />
        <MetricCard testid="stat-focus" icon={Clock} color="#5F7161" label={t("stats.focus_minutes")} value={Math.round(p.focus_minutes)} />
        <MetricCard testid="stat-games" icon={Target} color="#D0A98F" label={t("stats.games_played")} value={p.total_games} />
        <MetricCard testid="stat-wins" icon={Trophy} color="#E8A365" label={t("stats.total_wins")} value={p.total_wins} />
        <MetricCard testid="stat-clean-wins" icon={Sparkles} color="#5F7161" label={t("stats.clean_wins")} value={p.clean_wins || 0} />
        <MetricCard testid="stat-clean-streak" icon={Flame} color="#B85C2E" label={t("stats.clean_win_streak")} value={p.clean_win_streak || 0} />
      </div>

      {/* Iter69 — Weekly Champion badge moved to the Weekly Tournament
          page (now public, shows previous week's #1 finisher). Removed
          from Stats so it's not duplicated. */}

      <div className="p-8 rounded-3xl bg-white border border-[#E6E2D8]">
        <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light mb-6 flex items-center gap-2">
          <Activity strokeWidth={1.5} className="text-[#5F7161]" /> {t("stats.recent_sessions")}
        </h2>
        {data.recent_sessions.length === 0 ? (
          <p className="text-[#2C332A]/60">{t("stats.no_games")}</p>
        ) : (
          <div className="divide-y divide-[#E6E2D8]">
            {data.recent_sessions.map((s) => (
              <div key={s.session_id} data-testid={`session-${s.session_id}`} className="py-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{s.level_id} · {s.layout}</p>
                  <p className="text-sm text-[#2C332A]/60">
                    {new Date(s.started_at).toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${s.status === "won" ? "text-[#5F7161]" : "text-[#D96C6C]"}`}>
                    {sessionStatus(s.status)}
                  </p>
                  <p className="text-xs text-[#2C332A]/60">
                    {t("stats.session_summary", { coins: s.coins_earned || 0, calories: s.calories_earned || 0 })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-8 rounded-3xl bg-white border border-[#E6E2D8]">
          <p className="text-sm uppercase tracking-[0.2em] text-[#8AA48F] mb-2">{t("stats.daily_streak")}</p>
          <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-4xl font-light">
            {t("stats.days_unit", { count: p.longest_streak, n: p.longest_streak })}
          </p>
          <p className="text-sm text-[#2C332A]/60 mt-1">{t("stats.longest_streak_caption", { current: p.current_streak })}</p>
        </div>
        <div data-testid="stat-win-streak" className="p-8 rounded-3xl bg-white border border-[#E6E2D8]">
          <p className="text-sm uppercase tracking-[0.2em] text-[#8AA48F] mb-2">{t("stats.consecutive_wins")}</p>
          <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-4xl font-light">
            {t("stats.in_a_row", { count: p.longest_win_streak ?? 0, n: p.longest_win_streak ?? 0 })}
          </p>
          <p className="text-sm text-[#2C332A]/60 mt-1">{t("stats.longest_win_caption", { current: p.win_streak ?? 0 })}</p>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div data-testid={testid} className="p-6 rounded-3xl bg-white border border-[#E6E2D8]">
      <Icon style={{ color }} strokeWidth={1.5} />
      <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#8AA48F]">{label}</p>
      <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-3xl font-light mt-1">{value}</p>
    </div>
  );
}
